import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCustomerPreview } from '../context/CustomerPreviewContext';
import { formatPhoneDisplay } from '../utils/phone';
import { formatCurrency } from '../utils/formatting';
import FlagChips from './customers/FlagChips';

// The sidebar ⌘K dropdown. State lives in hooks/useGlobalSearch.js; this
// renders it: a "Go to" page row, then customers / orders / vessels /
// appointments / team, each row carrying the detail a person actually uses
// to tell two "John Smith"s apart (phone, city, vessel, order status…).
// Fully keyboard-driven: ↑ ↓ move the active row, ↵ opens it, Esc closes.

const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' };
const ICONS = {
  pages: (
    <svg width="16" height="16" viewBox="0 0 24 24" {...stroke}>
      <path d="M5 12h14" /><path d="M13 6l6 6-6 6" />
    </svg>
  ),
  customers: (
    <svg width="16" height="16" viewBox="0 0 24 24" {...stroke}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
  ),
  orders: (
    <svg width="16" height="16" viewBox="0 0 24 24" {...stroke}>
      <path d="M9 2h6a1 1 0 0 1 1 1v2h2a1 1 0 0 1 1 1v15a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h2V3a1 1 0 0 1 1-1z" />
      <line x1="9" y1="11" x2="15" y2="11" /><line x1="9" y1="15" x2="15" y2="15" />
    </svg>
  ),
  vehicles: (
    <svg width="16" height="16" viewBox="0 0 24 24" {...stroke}>
      <path d="M3 16s2 2 9 2 9-2 9-2" /><path d="M5 16l2-9h10l2 9" /><path d="M12 4v3" /><path d="M9 7h6" />
    </svg>
  ),
  appointments: (
    <svg width="16" height="16" viewBox="0 0 24 24" {...stroke}>
      <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  users: (
    <svg width="16" height="16" viewBox="0 0 24 24" {...stroke}>
      <path d="M17 21v-2a4 4 0 0 0-3-3.87" /><path d="M7 21v-2a4 4 0 0 1 3-3.87" />
      <circle cx="9" cy="7" r="4" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  recent: (
    <svg width="16" height="16" viewBox="0 0 24 24" {...stroke}>
      <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
    </svg>
  ),
  frequent: (
    <svg width="16" height="16" viewBox="0 0 24 24" {...stroke}>
      <path d="M12 3l2.9 5.9 6.5.9-4.7 4.6 1.1 6.5L12 17.8 6.2 20.9l1.1-6.5L2.6 9.8l6.5-.9z" />
    </svg>
  ),
  search: (
    <svg width="14" height="14" viewBox="0 0 24 24" {...stroke}>
      <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
    </svg>
  ),
};

const GROUP_LABEL = {
  pages: 'Go to',
  customers: 'Customers',
  orders: 'Orders',
  vehicles: 'Vessels',
  appointments: 'Appointments',
  users: 'Team',
};
const TYPE_TO_GROUP = { page: 'pages', customer: 'customers', order: 'orders', vehicle: 'vehicles', appointment: 'appointments', user: 'users' };

// Wrap every query token found in `text` in <mark>, case-insensitively.
function Highlight({ text, tokens }) {
  const str = String(text ?? '');
  if (!tokens?.length || !str) return str;
  const escaped = tokens.filter(Boolean).map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const re = new RegExp(`(${escaped.join('|')})`, 'ig');
  const parts = str.split(re);
  return parts.map((part, i) =>
    re.test(part) && part ? <mark key={i}>{part}</mark> : <React.Fragment key={i}>{part}</React.Fragment>,
  );
}

const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) });
};
const fmtDateTime = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

// The one-line detail under the title, per entity.
function Meta({ item, tokens }) {
  const bits = [];
  switch (item.type) {
    case 'customer':
      if (item.phone) bits.push(<Highlight key="p" text={formatPhoneDisplay(item.phone)} tokens={tokens} />);
      if (item.email) bits.push(<Highlight key="e" text={item.email} tokens={tokens} />);
      if (item.city) bits.push(<Highlight key="c" text={item.city} tokens={tokens} />);
      if (item.order_count) bits.push(`${item.order_count} order${item.order_count === 1 ? '' : 's'}`);
      break;
    case 'order':
      if (item.status) {
        bits.push(
          <span key="s" className="search-status">
            <span className="search-status-dot" style={item.status_color ? { background: item.status_color } : undefined} />
            {item.status}
          </span>,
        );
      }
      if (item.vessel) bits.push(<Highlight key="v" text={item.vessel} tokens={tokens} />);
      if (item.total_cents != null) bits.push(formatCurrency(item.total_cents));
      if (item.date) bits.push(fmtDate(item.date));
      break;
    case 'vehicle':
      if (item.owner) bits.push(<Highlight key="o" text={item.owner} tokens={tokens} />);
      if (item.length) bits.push(`${item.length}′`);
      if (item.hin) bits.push(<Highlight key="h" text={`HIN ${item.hin}`} tokens={tokens} />);
      if (item.order_count) bits.push(`${item.order_count} order${item.order_count === 1 ? '' : 's'}`);
      break;
    case 'appointment':
      if (item.date) bits.push(fmtDateTime(item.date));
      if (item.status) bits.push(item.status);
      break;
    case 'user':
      if (item.role) bits.push(String(item.role).replace(/_/g, ' '));
      break;
    default:
      break;
  }
  if (!bits.length && !item.subtitle) return null;
  return (
    <div className="search-result-meta">
      {item.subtitle && <span className="search-result-subtitle"><Highlight text={item.subtitle} tokens={tokens} /></span>}
      {bits.map((b, i) => <span key={i} className="search-result-bit">{b}</span>)}
    </div>
  );
}

function Row({ item, group, active, tokens, onSelect, onHover, index, badge }) {
  const ref = useRef(null);
  useEffect(() => {
    if (active) ref.current?.scrollIntoView?.({ block: 'nearest' });
  }, [active]);
  return (
    <div
      ref={ref}
      role="option"
      aria-selected={active}
      id={`search-opt-${index}`}
      className={`search-result-item${active ? ' active' : ''}`}
      data-entity={group}
      onMouseDown={(e) => e.preventDefault()} // keep the input focused
      onMouseEnter={() => onHover(index)}
      onClick={() => onSelect(item)}
    >
      <div className="search-result-icon" data-entity={group}>{ICONS[group] || ICONS.orders}</div>
      <div className="search-result-content">
        <div className="search-result-title">
          <Highlight text={item.title} tokens={tokens} />
          {item.type === 'customer' && item.flags?.length > 0 && <> <FlagChips flags={item.flags} /></>}
        </div>
        <Meta item={item} tokens={tokens} />
      </div>
      {badge && !active && <span className="search-result-badge">{badge}</span>}
      {active && <span className="search-result-enter" aria-hidden="true">↵</span>}
    </div>
  );
}

function SearchResults({ search, onNavigateAll }) {
  const navigate = useNavigate();
  const { openCustomerPreview } = useCustomerPreview();
  const { query, setQuery, loading, groups, counts, history, showingHistory, clearHistory, remember, activeIndex, setActiveIndex, navItems, close, minChars } = search;
  // A search running past a couple of seconds says so, rather than looking hung.
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!loading) { setSlow(false); return undefined; }
    const t = setTimeout(() => setSlow(true), 2500);
    return () => clearTimeout(t);
  }, [loading]);
  const tokens = query.trim().split(/\s+/).filter(Boolean).map((t) => t.replace(/^#/, ''));

  const handleSelect = (item) => {
    if (item.type === 'search') {
      setQuery(item.title); // re-run a past search; the box stays open
      return;
    }
    remember(item);
    if (item.type === 'customer' && item.id) {
      openCustomerPreview(item.id); // the global slide-in drawer, no navigation
    } else if (item.url) {
      navigate(item.url);
    }
    close();
  };
  // Expose the selector so the input's Enter key reaches it (see AdminLayout).
  search.selectRef.current = handleSelect;

  const trimmed = query.trim();
  let flatIndex = -1;

  if (showingHistory) {
    // Keyboard order mirrors navItems: recent visits → searches → most visited.
    const recentStart = 0;
    const searchStart = history.recent.length;
    const frequentStart = searchStart + history.searches.length;
    return (
      <div className="search-results-dropdown" role="listbox">
        {history.recent.length > 0 && (
          <div className="search-results-group" data-entity="recent">
            <div className="search-results-group-header">
              <span className="search-results-group-header-label">Recent</span>
              <button type="button" className="search-results-clear" onClick={clearHistory}>Clear history</button>
            </div>
            {history.recent.map((item, i) => (
              <Row key={`r-${item.type}-${item.id}`} item={item} group={TYPE_TO_GROUP[item.type] || 'recent'} active={recentStart + i === activeIndex} index={recentStart + i} tokens={[]} onSelect={handleSelect} onHover={setActiveIndex} />
            ))}
          </div>
        )}
        {history.searches.length > 0 && (
          <div className="search-results-group" data-entity="searches">
            <div className="search-results-group-header">
              <span className="search-results-group-header-label">Recent searches</span>
            </div>
            <div className="search-chips">
              {history.searches.map((q, i) => (
                <button
                  type="button"
                  key={q}
                  id={`search-opt-${searchStart + i}`}
                  role="option"
                  aria-selected={searchStart + i === activeIndex}
                  className={`search-chip${searchStart + i === activeIndex ? ' active' : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setActiveIndex(searchStart + i)}
                  onClick={() => handleSelect({ type: 'search', id: q, title: q })}
                >
                  {ICONS.search}
                  <span>{q}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {history.frequent.length > 0 && (
          <div className="search-results-group" data-entity="frequent">
            <div className="search-results-group-header">
              <span className="search-results-group-header-label">Most visited</span>
            </div>
            {history.frequent.map((item, i) => (
              <Row key={`f-${item.type}-${item.id}`} item={item} group={TYPE_TO_GROUP[item.type] || 'frequent'} active={frequentStart + i === activeIndex} index={frequentStart + i} tokens={[]} onSelect={handleSelect} onHover={setActiveIndex} badge={`${item.count}×`} />
            ))}
          </div>
        )}
        <Footer />
      </div>
    );
  }

  if (trimmed.length < minChars) {
    return (
      <div className="search-results-dropdown">
        <div className="search-results-hint">Search customers, orders, vessels, appointments and team — by name, phone, email, order # or HIN. Things you open will show up here.</div>
      </div>
    );
  }

  const nothing = !loading && groups.length === 0;

  return (
    <div className="search-results-dropdown" role="listbox" aria-busy={loading}>
      {loading && (
        <div className="search-progress" role="status" aria-live="polite">
          <span className="search-progress-bar" aria-hidden="true" />
          <span className="search-progress-label">
            <span className="search-spinner" aria-hidden="true" />
            {slow ? 'Still searching' : 'Searching'} for “{trimmed}”…
          </span>
        </div>
      )}
      {loading && groups.length === 0 && (
        <div className="search-skeleton" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="search-skeleton-row">
              <span className="search-skeleton-icon" />
              <span className="search-skeleton-lines">
                <span className="search-skeleton-line" style={{ width: `${62 - i * 12}%` }} />
                <span className="search-skeleton-line short" style={{ width: `${40 - i * 6}%` }} />
              </span>
            </div>
          ))}
        </div>
      )}
      <div className={`search-results-body${loading && groups.length ? ' is-stale' : ''}`}>
      {nothing ? (
        <div className="search-results-empty">
          <div>No matches for “{trimmed}”</div>
          <div className="search-results-hint">Try a last name, a phone number, an order # or a vessel's HIN.</div>
        </div>
      ) : (
        groups.map((g) => (
          <div key={g.key} className="search-results-group" data-entity={g.key}>
            <div className="search-results-group-header">
              <span className="search-results-group-header-label">{GROUP_LABEL[g.key] || g.key}</span>
              {g.count > g.items.length && (
                g.key === 'customers' && onNavigateAll
                  ? <button type="button" className="search-results-more" onClick={() => { onNavigateAll(trimmed); close(); }}>All {g.count} →</button>
                  : <span className="search-results-group-count">+{g.count - g.items.length} more</span>
              )}
            </div>
            {g.items.map((item) => {
              flatIndex += 1;
              const idx = flatIndex;
              return (
                <Row key={`${item.type}-${item.id}`} item={item} group={TYPE_TO_GROUP[item.type] || g.key} active={idx === activeIndex} index={idx} tokens={tokens} onSelect={handleSelect} onHover={setActiveIndex} />
              );
            })}
          </div>
        ))
      )}
      </div>
      <Footer total={navItems.length} counts={counts} />
    </div>
  );
}

function Footer() {
  return (
    <div className="search-results-footer">
      <span><kbd>↑</kbd><kbd>↓</kbd> move</span>
      <span><kbd>↵</kbd> open</span>
      <span><kbd>esc</kbd> close</span>
    </div>
  );
}

export default SearchResults;
